import { DeleteOutlined } from "@ant-design/icons"
import { Button, DatePicker, Flex, Form, Space } from "antd"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { Text } from "ui/Typography"
import { DATE_FORMAT, DATE_PICKER_PROPS } from "pagesV2/pro/Experiences/ListingScheduling/utils/constants"
import dayjs from "dayjs"

const ICON_WIDTH = 32

export const DateRangeBlock = ({
  blockIndex,
  onDelete,
  isSeasonalActive,
  initialStartDate,
  initialEndDate,
  allRanges = [],
  timeBlocks
}) => {
  const { t } = useTranslation()
  const form = Form.useFormInstance()

  const handleStartDateChange = () => {
    const startDate = form.getFieldValue(`seasonStart_${blockIndex}`)
    const endDate = form.getFieldValue(`seasonEnd_${blockIndex}`)
    if (endDate && startDate && endDate.isBefore(startDate)) {
      form.setFieldValue(`seasonEnd_${blockIndex}`, undefined)
    }
  }

  const isDateDisabled = (current) => {
    return allRanges.some((range, idx) => {
      const selfCheckIndex = typeof range.blockIndex === "number" ? range.blockIndex : idx
      if (selfCheckIndex === blockIndex) return false

      const { start, end } = range
      const currentDate = dayjs(current)
      const startDate = dayjs(start)
      const endDate = dayjs(end)

      return currentDate.isAfter(startDate.subtract(1, "day")) && currentDate.isBefore(endDate.add(1, "day"))
    })
  }

  const isEndDateDisabled = (current) => {
    const startDate = form.getFieldValue(`seasonStart_${blockIndex}`)
    const isBeforeStart = current && startDate && dayjs(current).isBefore(dayjs(startDate))
    return isBeforeStart || isDateDisabled(current)
  }

  return (
    <div className="mb-l">
      <Space direction="vertical" className="w-full">
        <Flex className="w-full">
          <div className="flex-1">
            <Text fw="600">
              {t("global.date_range.from")}
              {isSeasonalActive && <span className="text-red-500 ml-1">*</span>}
            </Text>
          </div>
          <div className="flex-1">
            <Text fw="600">
              {t("global.date_range.to")}
              {isSeasonalActive && <span className="text-red-500 ml-1">*</span>}
            </Text>
          </div>
          <div style={{ width: ICON_WIDTH }} />
        </Flex>

        <Flex gap="middle" align="center">
          <Form.Item
            name={`seasonStart_${blockIndex}`}
            rules={[
              {
                required: isSeasonalActive,
                message: t("global.date_range.start_validation")
              }
            ]}
            initialValue={initialStartDate}
            className="mb-0 flex-1"
          >
            <DatePicker
              format={DATE_FORMAT}
              placeholder={t("global.date_range.placeholder")}
              style={{ width: "100%" }}
              inputProps={DATE_PICKER_PROPS}
              onChange={handleStartDateChange}
              disabledDate={isDateDisabled}
            />
          </Form.Item>

          <Form.Item
            name={`seasonEnd_${blockIndex}`}
            rules={[
              {
                required: isSeasonalActive,
                message: t("global.date_range.end_validation")
              }
            ]}
            initialValue={initialEndDate}
            className="mb-0 flex-1"
          >
            <DatePicker
              format={DATE_FORMAT}
              placeholder={t("global.date_range.placeholder")}
              style={{ width: "100%" }}
              inputProps={DATE_PICKER_PROPS}
              disabledDate={isEndDateDisabled}
            />
          </Form.Item>

          <div style={{ width: ICON_WIDTH, display: "flex", justifyContent: "center" }}>
            {timeBlocks?.length > 1 && (
              <Button
                type="default"
                icon={<DeleteOutlined />}
                onClick={() => onDelete(blockIndex)}
                shape="circle"
                className="flex items-center justify-center"
              />
            )}
          </div>
        </Flex>
      </Space>
    </div>
  )
}

DateRangeBlock.propTypes = {
  blockIndex: PropTypes.number.isRequired,
  onDelete: PropTypes.func.isRequired,
  isSeasonalActive: PropTypes.bool.isRequired,
  initialStartDate: PropTypes.object,
  initialEndDate: PropTypes.object,
  timeBlocks: PropTypes.array,
  allRanges: PropTypes.arrayOf(
    PropTypes.shape({
      blockIndex: PropTypes.number,
      start: PropTypes.object.isRequired,
      end: PropTypes.object.isRequired
    })
  )
}
